import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { API_ERROR_MESSAGES, GET_POLLING_INTERVAL } from 'src/utils/constants';

import axios from 'src/lib/axios';

import { PipelineName } from './useCreateRun';

const MAX_REFETCH_ATTEMPTS = 35;

interface RunResponse {
  run_id: string;
  status: string;
  details?: {
    [K in `${PipelineName}_response`]?: {
      results?: {
        chatbot_module?: string;
        llm_test_module?: string;
      };
    };
  };
  [key: string]: any;
}
function getErrorMessage(errorMessage: string): string {
  if (errorMessage.includes('too many images and documents'))
    return API_ERROR_MESSAGES.MAX_PAGES_LIMIT_EXCEEDED;
  else if (errorMessage.includes('maximum context length'))
    return API_ERROR_MESSAGES.CONTEXT_LIMIT_EXCEEDED;
  else if (errorMessage.includes('missing 1 required positional argument'))
    return API_ERROR_MESSAGES.BAD_REQUEST_ERROR;
  return API_ERROR_MESSAGES.GENERIC_ERROR;
}
const getRun = async (runId?: string): Promise<RunResponse | null> => {
  const response = await axios.get(`/run_config/${runId}`);

  if (response.data.status === 'FAILED') {
    const currentPipeLineName =
      response.data.config.pipelines[0].name === PipelineName.CHAT_WITH_AUDIO
        ? PipelineName.CHAT_WITH_AUDIO
        : PipelineName.CHATBOT + '_response';
    const error = getErrorMessage(response.data.details?.[currentPipeLineName]?.error || '');
    throw new Error(error);
  }

  return response.data as RunResponse;
};

export const useRun = (initialRunId?: string, fileSize?: number, fileId?: string) => {
  const refetchCountRef = useRef(0);
  const currentRunIdRef = useRef(initialRunId);
  const maxAttemptsErrorRef = useRef<Error | null>(null);

  // Reset counter when runId changes
  if (currentRunIdRef.current !== initialRunId) {
    refetchCountRef.current = 0;
    currentRunIdRef.current = initialRunId;
    maxAttemptsErrorRef.current = null; // Reset error when runId changes
  }
  const { data, error, isFetching, isPending } = useQuery({
    queryKey: ['run', initialRunId],
    queryFn: async () => {
      // Increment counter on each API call
      refetchCountRef.current += 1;
      const result = await getRun(initialRunId);
      return result;
    },
    enabled: !!initialRunId,
    refetchInterval: (query) => {
      const { data: responseData, error: queryError } = query.state;
      // Stop if error
      if (queryError) {
        return false;
      }

      // Stop if completed/failed
      if (responseData?.status === 'COMPLETED' || responseData?.status === 'FAILED') {
        refetchCountRef.current = 0;
        maxAttemptsErrorRef.current = null;
        return false;
      }

      // Stop if max attempts reached
      if (refetchCountRef.current > MAX_REFETCH_ATTEMPTS) {
        console.warn(`Maximum refetch attempts (${MAX_REFETCH_ATTEMPTS}) reached`);
        return false;
      }
      return GET_POLLING_INTERVAL(fileSize ?? 0);
    },
    refetchOnWindowFocus: false,
  });

  // Create max attempts error only once using useRef
  if (
    refetchCountRef.current > MAX_REFETCH_ATTEMPTS &&
    data?.status !== 'COMPLETED' &&
    data?.status !== 'FAILED' &&
    !error &&
    !maxAttemptsErrorRef.current
  ) {
    maxAttemptsErrorRef.current = new Error('Something went wrong, please try again later.');
  }

  return {
    response: data,
    error: error ?? maxAttemptsErrorRef.current,
    isFetchingResponse: isFetching,
    isPendingResponse: isPending,
    refetchCount: refetchCountRef.current,
    fileId,
  };
};
